import { useEffect } from "react";
import { GetServerSidePropsContext } from "next";

import { useRouter } from "next/router";
import { LoadingSpinner } from "@newsela/angelou";
import NotLoggedInError from "@/errors/NotLoggedInError";
import BaseLayout from "@/base";
import RootProviders from "@/base/RootProviders";
import TopicPage from "@/browse/TopicPage/";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";

import { useGetUser } from "@/users/v3";

export const BROWSE_PAGE_PRODUCT_IDS = [
  "ckd51x1pr00023hoalzrknglr", // ELA
  "ckd0kn7y000013hqqp3k7t2yx", // Social Studies
  "ckd0jvn3z00023jn8mkcw1jjd", // Science
  "ckd4tly7100003dmt4gfi3fw6", // Essentials
];

const BrowsePageRoute = () => {
  const { push, query } = useRouter();
  const contentId = query["contentId"] as string;

  const { data: user, isLoading: isLoadingUser } = useGetUser();

  const [newBundlesExperience] = useGetUserExperimentSegment(
    "bundles_page_rebuild",
  );
  const isBundleExperimentActive = newBundlesExperience === "on";
  const canSeeNewBundlesExperience =
    isBundleExperimentActive && BROWSE_PAGE_PRODUCT_IDS.includes(contentId);

  useEffect(() => {
    if (newBundlesExperience) {
      if (canSeeNewBundlesExperience) {
        // Redirect to new bundles experience.
        push(`/content/subject/${contentId}`);
      }
    }
  }, [newBundlesExperience, canSeeNewBundlesExperience]);

  if (isLoadingUser) {
    return <BaseLayout mainContents={<LoadingSpinner />} />;
  }

  if (!user?.is_authenticated) {
    return <BaseLayout mainContents={<NotLoggedInError />} />;
  }

  // @ts-ignore: Ignoring until we can convert TopicPage to TS.
  return <BaseLayout mainContents={<TopicPage />} />;
};

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const RootProvidersInitialProps = await RootProviders.__getInitialProps({
    ctx,
  });
  const { data: user } =
    RootProvidersInitialProps.userProviderProps.userQueryInitialValue;

  if (!user || !user.unique_id) {
    const signInUrl = "/signin";
    return {
      redirect: {
        destination: signInUrl,
        permanent: false,
      },
    };
  }

  // This feature _should_ be enabled for all adult roles.
  // Most students should also have this feature. It is only false
  // for those operating in a content restricted environment where the customer
  // has explicitly asked to not let the student browse around.
  if (user.features?.can_view_search === "on") {
    return {
      props: {
        [RootProviders.InitialPropsName]: RootProvidersInitialProps,
      },
    };
  }

  // Only a subset of students will end up here. Adults are handled above.
  return {
    props: {},
    redirect: {
      destination: "/assignments/",
      permanent: false,
    },
  };
};

export default BrowsePageRoute;
